
import Vue from 'vue';
import { Icon, Button, Tooltip } from '@descomplica/vue-components';
import StepProgressBar from '../../progress/StepProgressBar.vue';
import { isMobileDevice } from '@/helpers/mobile';
import { ClassroomModel, CourseModel } from '@descomplica/dex-models';
import { getDexToken } from '@/helpers/get-tokens';
import MicrocertificateRequestsService from '@/helpers/postgradMicrocertificate/request';
import MicrocertificateDownloadModal from '../../modals/microcertificate/postgrad/MicrocertificateDownloadModal.vue';
import InvalidNameModal from '../../modals/microcertificate/postgrad/InvalidNameModal.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getCourseCategory } from '@/helpers/category';

interface Data {
  showInvalidNameModal: boolean;
  showMicrocertificateDownloadModal: boolean;
  showDisabledButtonTooltip: boolean;
  isLoadingMicrocertificateNameEligibility: boolean;
}

export default Vue.extend({
  components: {
    StepProgressBar,
    Icon,
    Button,
    Tooltip,
    MicrocertificateDownloadModal,
    InvalidNameModal,
  },
  props: {
    classroom: {
      required: true,
      type: Object as Vue.PropType<ClassroomModel>,
    },
  },
  data(): Data {
    return {
      showDisabledButtonTooltip: false,
      isLoadingMicrocertificateNameEligibility: false,
      showMicrocertificateDownloadModal: false,
      showInvalidNameModal: false,
    };
  },
  computed: {
    hasMicrocertificateEnabled(): boolean {
      return this.$store.getters.microcertificateEligibilityByClassroomId(this.classroom.id);
    },
    isMicrocertificateDownloadAvailable(): boolean {
      return this.hasMicrocertificateEnabled && !this.isLoadingMicrocertificateNameEligibility;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    linkedinOAuthCode(): string {
      return (this.$route.query.code as string) ?? '';
    },
  },
  mounted() {
    if (!this.hasMicrocertificateEnabled) {
      this.$store.dispatch('GET_DISCIPLINE_MICROCERTIFICATE_ELIGIBILITY', this.classroom.id);
    }

    if (this.linkedinOAuthCode) this.showMicrocertificateDownloadModal = true;
  },
  methods: {
    async checkNameEligibility(): Promise<boolean> {
      const token = getDexToken();
      const course = this.$store.getters.currentCourse;
      if (!token) return false;

      const isEligible = await MicrocertificateRequestsService.getMicrocertificateNameEligibility({
        course,
        token,
      });

      return isEligible;
    },

    async checkMicrocertificateUserName() {
      if (!this.isMicrocertificateDownloadAvailable) return;

      this.trackMicrocertificateDownloadButtonClicked();

      this.isLoadingMicrocertificateNameEligibility = true;
      const isNameEligible = await this.checkNameEligibility();
      if (!isNameEligible) {
        this.toggleNameUpdateModal();
        this.isLoadingMicrocertificateNameEligibility = false;

        return;
      }

      this.isLoadingMicrocertificateNameEligibility = false;

      this.toggleMicrocertificateDownloadModal();
    },

    toggleDisabledButtonTooltip(): void {
      if (isMobileDevice() || this.hasMicrocertificateEnabled) return;

      this.showDisabledButtonTooltip = !this.showDisabledButtonTooltip;
    },

    toggleMicrocertificateDownloadModal(): void {
      this.showMicrocertificateDownloadModal = !this.showMicrocertificateDownloadModal;
    },

    toggleNameUpdateModal(): void {
      this.showInvalidNameModal = !this.showInvalidNameModal;
    },

    trackMicrocertificateDownloadButtonClicked(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_download_postgrad_microcertificate: true,
        is_postgrad_microcertificate_available: this.hasMicrocertificateEnabled,
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
      });
    },

    trackProgressBarClick(): void {
      Analytics.trackEvent(EVENT_LABELS.click, {
        in_postgrad_microcertificate_progress_bar: true,
        is_postgrad_microcertificate_available: this.hasMicrocertificateEnabled,
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
      });
    },
  },
});
